import React, { useCallback, useEffect, useMemo, useState } from "react";
import { userApi, publicApi, formatUSD, formatLBP } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Boxes, CheckCircle2, Edit3, History, Lock, Plus, RefreshCw, Shield, Sparkles, Target, Trash2, Users, Wallet, X } from "lucide-react";
import LbpAmountInput from "@/components/ui/LbpAmountInput";

const requestId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
const money = (amount, currency) =>
  currency === "USD" ? `$${formatUSD(amount)}` : `${formatLBP(amount)} ل.ل`;
const labels = {
  REGULAR: "Box",
  GOAL: "Goal Box",
  PERSONAL: "شخصي",
  FAMILY: "عائلي",
};
const activityLabels = {
  BOX_CREATED: "تم إنشاء الصندوق",
  BOX_FUNDED: "تمت إضافة أموال",
  FAMILY_CONTRIBUTION: "مساهمة عائلية",
  BOX_WITHDRAWN: "إعادة إلى رصيد البنك",
  BOX_SPENT: "مصروف من الصندوق",
  BOX_LOCKED: "تم قفل الصندوق",
  BOX_UNLOCKED: "تم فتح الصندوق",
  EMERGENCY_UNLOCK_REQUESTED: "طلب فتح طارئ",
  EMERGENCY_UNLOCK_APPROVED: "تمت الموافقة على الفتح",
  EMERGENCY_UNLOCK_REJECTED: "تم رفض الفتح",
  GOAL_75_PERCENT: "وصل الهدف إلى 75%",
  GOAL_COMPLETED: "اكتمل الهدف",
  GOAL_TARGET_CHANGED: "تغير مبلغ الهدف",
  GOAL_EXCESS_RETURNED: "أعيد المبلغ الزائد",
  FAMILY_MEMBER_LEFT: "غادر عضو",
  FAMILY_CONTRIBUTION_RETURNED: "أعادت الإدارة مساهمة",
};

export default function GoalsTab({ me, onUpdate }) {
  const [boxes, setBoxes] = useState([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [filter, setFilter] = useState("ALL"),
    [createOpen, setCreateOpen] = useState(false),
    [selected, setSelected] = useState(null),
    [limit, setLimit] = useState(null),
    [celebration, setCelebration] = useState(null);
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await userApi.get("/user/boxes");
      setBoxes(data.boxes || []);
      setLimit(data.effective_limit);
      if (selected)
        setSelected(
          (data.boxes || []).find((row) => row.id === selected.id) || null,
        );
    } catch (e) {
      setError(e?.response?.data?.detail || "تعذر تحميل الصناديق.");
    } finally {
      setLoading(false);
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async (completedName) => {
    await load();
    await onUpdate?.();
    if (completedName) {
      setCelebration(completedName);
      setTimeout(() => setCelebration(null), 4500);
    }
  };
  const shown = boxes.filter((box) =>
    filter === "ALL" || filter === "FAMILY"
      ? filter === "ALL" || box.scope === "FAMILY"
      : filter === "COMPLETED"
        ? box.status === "COMPLETED"
        : box.type === filter,
  );
  const totals = useMemo(
    () =>
      boxes.reduce(
        (sum, box) => ({
          USD: sum.USD + Number(box.balances?.USD || 0),
          LBP: sum.LBP + Number(box.balances?.LBP || 0),
        }),
        { USD: 0, LBP: 0 },
      ),
    [boxes],
  );
  const remove = async (box) => {
    if (
      !window.confirm(
        `حذف "${box.name}"؟ سيُعاد تلقائياً ${money(box.balances.USD, "USD")} و${money(box.balances.LBP, "LBP")} إلى رصيد صاحب الصندوق، مع إبقاء السجل التاريخي.`,
      )
    )
      return;
    try {
      const { data } = await userApi.delete(`/user/boxes/${box.id}`, {
        data: { request_id: requestId("delete") },
      });
      toast.success(
        `حُذف الصندوق وأعيد ${money(data.returned.USD, "USD")} و${money(data.returned.LBP, "LBP")}.`,
      );
      setSelected(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر حذف الصندوق.");
    }
  };
  return (
    <div className="space-y-6" data-testid="goals-boxes-page">
      {celebration && (
        <div className="fixed inset-x-4 top-24 z-50 mx-auto max-w-lg rounded-3xl border border-amber-300 bg-gradient-to-l from-amber-400 to-yellow-200 p-6 text-center text-slate-950 shadow-2xl">
          <Sparkles className="mx-auto h-9 w-9" />
          <b className="mt-2 block text-xl">🎉 اكتمل الهدف!</b>
          <span>{celebration}</span>
        </div>
      )}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="section-title text-2xl sm:text-3xl">
            الأهداف والصناديق
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-[var(--color-text-secondary)]">
            Box للادخار بحرية، وGoal Box للادخار نحو مبلغ محدد. كل الحركات هنا
            داخل بنك العائلة ولا تحتاج موافقة.
          </p>
        </div>
        <button
          disabled={Boolean(me?.view_as?.read_only)} onClick={() => setCreateOpen(true)}
          className="btn-navy min-h-11 rounded-xl px-5 text-sm font-black"
        >
          <Plus className="me-2 inline h-4 w-4" />
          إنشاء Box
        </button>
      </header>
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Summary
          icon={Boxes}
          label="إجمالي الصناديق"
          value={`${boxes.length}${limit === null ? " / غير محدود" : ` / ${limit}`}`}
        />
        <Summary
          icon={Wallet}
          label="المدخر بالدولار"
          value={money(totals.USD, "USD")}
        />
        <Summary
          icon={Target}
          label="الأهداف النشطة"
          value={
            boxes.filter((x) => x.type === "GOAL" && x.status !== "COMPLETED")
              .length
          }
        />
        <Summary
          icon={CheckCircle2}
          label="الأهداف المكتملة"
          value={boxes.filter((x) => x.status === "COMPLETED").length}
        />
      </section>
      <div className="flex gap-2 overflow-x-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-2">
        {[
          ["ALL", "الكل"],
          ["REGULAR", "Boxes"],
          ["GOAL", "Goal Boxes"],
          ["FAMILY", "العائلة"],
          ["COMPLETED", "مكتملة"],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`min-h-10 shrink-0 rounded-xl px-4 text-xs font-bold ${filter === id ? "bg-[var(--color-navigation)] text-white" : "hover:bg-[var(--color-surface-secondary)]"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {error ? (
        <State icon={AlertTriangle} title={error}>
          <button onClick={load} className="rounded-xl border px-4 py-2">
            <RefreshCw className="me-2 inline h-4 w-4" />
            إعادة المحاولة
          </button>
        </State>
      ) : loading ? (
        <State icon={RefreshCw} title="جارٍ تحميل الصناديق…" />
      ) : shown.length === 0 ? (
        <State icon={Shield} title="لا تملك أي صناديق هنا بعد">
          <p className="text-sm text-[var(--color-text-secondary)]">
            أنشئ Box للادخار بحرية أو Goal Box لهدف واضح.
          </p>
          <button
            disabled={Boolean(me?.view_as?.read_only)} onClick={() => setCreateOpen(true)}
            className="btn-navy rounded-xl px-5 py-2.5"
          >
            إنشاء أول صندوق
          </button>
        </State>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((box) => (
            <BoxCard
              key={box.id}
              box={box}
              readOnly={Boolean(me?.view_as?.read_only)}
              onOpen={() => setSelected(box)}
              onDelete={() => remove(box)}
            />
          ))}
        </div>
      )}
      {createOpen && (
        <CreateModal
          me={me}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refresh();
          }}
        />
      )}
      {selected && (
        <DetailsModal
          box={selected}
          me={me}
          onClose={() => setSelected(null)}
          onRefresh={refresh}
          onDelete={() => remove(selected)}
        />
      )}
    </div>
  );
}

function Summary({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <Icon className="h-5 w-5 text-[var(--color-accent)]" />
      <small className="mt-3 block text-[var(--color-text-secondary)]">
        {label}
      </small>
      <b className="mt-1 block text-xl font-black">{value}</b>
    </div>
  );
}
function State({ icon: Icon, title, children }) {
  return (
    <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-10 text-center">
      <Icon className="mx-auto h-12 w-12 text-amber-500" />
      <h3 className="mt-4 font-black">{title}</h3>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}
function BoxCard({ box, onOpen, onDelete, readOnly }) {
  const p = box.progress;
  return (
    <article className="overflow-hidden rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-sm">
      <button onClick={onOpen} className="w-full p-5 text-start">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 gap-3">
            <span className="rounded-2xl bg-[var(--color-surface-secondary)] p-3 text-3xl">
              {box.icon}
            </span>
            <div className="min-w-0">
              <h3 className="truncate font-black">{box.name}</h3>
              <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                <Tag>{labels[box.type]}</Tag>
                <Tag>{labels[box.scope]}</Tag>
                {box.is_locked && (
                  <Tag>
                    <Lock className="inline h-3 w-3" /> مقفل
                  </Tag>
                )}
                {box.status === "COMPLETED" && <Tag>✓ مكتمل</Tag>}
              </div>
            </div>
          </div>
          <span className="text-xs text-[var(--color-text-secondary)]">
            التفاصيل ←
          </span>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <Balance currency="USD" amount={box.balances.USD} />
          <Balance currency="LBP" amount={box.balances.LBP} />
        </div>
        {p && (
          <div className="mt-4">
            <div className="flex justify-between text-xs font-bold">
              <span>
                {money(p.saved_amount, p.currency)} /{" "}
                {money(p.target_amount, p.currency)}
              </span>
              <span>{p.percentage}%</span>
            </div>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[var(--color-surface-secondary)]">
              <div
                className="h-full rounded-full bg-gradient-to-l from-amber-400 to-amber-600"
                style={{ width: `${p.percentage}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
              المتبقي {money(p.remaining_amount, p.currency)}
            </p>
          </div>
        )}
      </button>
      <div className="border-t border-[var(--color-border)] p-2 text-end">
        <button
          disabled={readOnly} onClick={onDelete}
          className="min-h-9 rounded-xl px-3 text-xs font-bold text-red-600 hover:bg-red-50"
        >
          <Trash2 className="me-1 inline h-4 w-4" />
          حذف وإعادة الرصيد
        </button>
      </div>
    </article>
  );
}
function Tag({ children }) {
  return (
    <span className="rounded-full bg-[var(--color-surface-secondary)] px-2 py-1 font-bold">
      {children}
    </span>
  );
}
function Balance({ currency, amount }) {
  return (
    <div className="rounded-xl bg-[var(--color-surface-secondary)] p-3">
      <small className="text-[var(--color-text-secondary)]">{currency}</small>
      <b className="mt-1 block text-sm">{money(amount, currency)}</b>
    </div>
  );
}

function CreateModal({ me, onClose, onSaved }) {
  const [step, setStep] = useState(1),
    [users, setUsers] = useState([]),
    [loading, setLoading] = useState(false),
    [form, setForm] = useState({
      type: "REGULAR",
      scope: "PERSONAL",
      name: "",
      description: "",
      icon: "🛡️",
      target_amount: "",
      target_currency: "USD",
      target_date: "",
      lock_mode: "UNLOCKED",
      locked_until: "",
      member_ids: [],
    });
  useEffect(() => {
    publicApi
      .get("/public/users")
      .then(({ data }) => setUsers((data || []).filter((u) => u.id !== me?.id)))
      .catch(() => {});
  }, [me?.id]);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userApi.post("/user/boxes", {
        ...form,
        target_amount:
          form.target_currency === "LBP"
            ? Number(form.target_amount || 0) * 1000
            : form.target_amount,
        request_id: requestId("create"),
      });
      toast.success("تم إنشاء الصندوق وحفظه.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر إنشاء الصندوق.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal title="إنشاء Box" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {step === 1 ? (
          <>
            <p className="text-sm font-bold">ماذا تريد أن تنشئ؟</p>
            <div className="grid grid-cols-2 gap-3">
              <Choice
                active={form.type === "REGULAR"}
                onClick={() =>
                  setForm({ ...form, type: "REGULAR", icon: "🛡️" })
                }
                icon={Shield}
                title="Regular Box"
                text="ادخار مرن بلا هدف"
              />
              <Choice
                active={form.type === "GOAL"}
                onClick={() => setForm({ ...form, type: "GOAL", icon: "🎯" })}
                icon={Target}
                title="Goal Box"
                text="ادخار نحو هدف"
              />
            </div>
            <p className="text-sm font-bold">لمن الصندوق؟</p>
            <div className="grid grid-cols-2 gap-3">
              <Choice
                active={form.scope === "PERSONAL"}
                onClick={() =>
                  setForm({ ...form, scope: "PERSONAL", member_ids: [] })
                }
                icon={Wallet}
                title="شخصي"
                text="لك وحدك"
              />
              <Choice
                active={form.scope === "FAMILY"}
                onClick={() => setForm({ ...form, scope: "FAMILY" })}
                icon={Users}
                title="عائلي"
                text="مساهمات مشتركة"
              />
            </div>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="btn-navy w-full rounded-xl py-3"
            >
              التالي
            </button>
          </>
        ) : (
          <>
            <input
              required
              className="input-pro"
              placeholder={form.type === "GOAL" ? "اسم الهدف" : "اسم الصندوق"}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <textarea
              className="input-pro min-h-20"
              placeholder="وصف اختياري"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
            <div className="grid grid-cols-[5rem_1fr] gap-3">
              <input
                className="input-pro text-center text-2xl"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
              />
              <select
                className="input-pro"
                value={form.lock_mode}
                onChange={(e) =>
                  setForm({ ...form, lock_mode: e.target.value })
                }
              >
                <option value="UNLOCKED">غير مقفل</option>
                <option value="MANUAL">قفل يدوي</option>
                <option value="UNTIL">مقفل حتى تاريخ</option>
              </select>
            </div>
            {form.lock_mode === "UNTIL" && (
              <input
                required
                type="datetime-local"
                className="input-pro"
                value={form.locked_until}
                onChange={(e) =>
                  setForm({ ...form, locked_until: e.target.value })
                }
              />
            )}{" "}
            {form.type === "GOAL" && (
              <div className="grid grid-cols-[1fr_7rem] gap-3">
                {form.target_currency === "LBP" ? (
                  <LbpAmountInput currency="LBP"
                    required
                    value={form.target_amount}
                    onChange={(value) => setForm({ ...form, target_amount: value })}
                    placeholder="مبلغ الهدف"
                  />
                ) : (
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="input-pro"
                    placeholder="مبلغ الهدف"
                    value={form.target_amount}
                    onChange={(e) =>
                      setForm({ ...form, target_amount: e.target.value })
                    }
                  />
                )}
                <select
                  className="input-pro"
                  value={form.target_currency}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      target_currency: e.target.value,
                      target_amount: "",
                    })
                  }
                >
                  <option>USD</option>
                  <option>LBP</option>
                </select>
                <input
                  type="date"
                  className="input-pro col-span-2"
                  value={form.target_date}
                  onChange={(e) =>
                    setForm({ ...form, target_date: e.target.value })
                  }
                />
              </div>
            )}
            {form.scope === "FAMILY" && (
              <div>
                <label className="text-xs font-bold">المشاركون</label>
                <div className="mt-2 max-h-36 space-y-1 overflow-auto rounded-xl border p-2">
                  {users.map((u) => (
                    <label
                      key={u.id}
                      className="flex min-h-10 items-center gap-2 rounded-lg px-2 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={form.member_ids.includes(u.id)}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            member_ids: e.target.checked
                              ? [...form.member_ids, u.id]
                              : form.member_ids.filter((id) => id !== u.id),
                          })
                        }
                      />
                      <span className="text-sm">
                        {u.display_name} (@{u.username})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-xl border px-4"
              >
                السابق
              </button>
              <button
                disabled={loading}
                className="btn-navy flex-1 rounded-xl py-3"
              >
                {loading ? "جارٍ الحفظ…" : "إنشاء وحفظ"}
              </button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}
function Choice({ active, onClick, icon: Icon, title, text }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-28 rounded-2xl border p-4 text-start ${active ? "border-amber-500 bg-amber-50 text-slate-950 ring-2 ring-amber-200" : "border-[var(--color-border)]"}`}
    >
      <Icon className="h-5 w-5" />
      <b className="mt-2 block text-sm">{title}</b>
      <small>{text}</small>
    </button>
  );
}

function DetailsModal({ box, me, onClose, onRefresh, onDelete }) {
  const [action, setAction] = useState(null),
    owner = box.owner_id === me?.id,
    can = (key) =>
      owner ||
      box.my_permissions.includes("ALL") ||
      box.my_permissions.includes(key);
  return (
    <Modal title={box.name} onClose={onClose} wide>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <Balance currency="USD" amount={box.balances.USD} />
          <Balance currency="LBP" amount={box.balances.LBP} />
        </div>
        {box.progress && (
          <div className="rounded-2xl bg-[var(--color-surface-secondary)] p-4">
            <div className="flex justify-between font-bold">
              <span>
                {money(box.progress.saved_amount, box.progress.currency)} /{" "}
                {money(box.progress.target_amount, box.progress.currency)}
              </span>
              <span>{box.progress.percentage}%</span>
            </div>
            <div className="mt-3 h-3 rounded-full bg-white/60">
              <div
                className="h-3 rounded-full bg-amber-500"
                style={{ width: `${box.progress.percentage}%` }}
              />
            </div>
            <p className="mt-2 text-xs">
              المتبقي{" "}
              {money(box.progress.remaining_amount, box.progress.currency)}
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Action
            onClick={() => setAction("CONTRIBUTE")}
            icon={ArrowDownLeft}
            label="إضافة أموال"
          />
          <Action
            disabled={!can("WITHDRAW") || box.is_locked}
            onClick={() => setAction("WITHDRAW")}
            icon={ArrowUpRight}
            label="إلى رصيد البنك"
          />
          <Action
            disabled={!can("SPEND") || box.is_locked}
            onClick={() => setAction("SPEND")}
            icon={Wallet}
            label="صرف مباشر"
          />
          <Action
            disabled={!can("EDIT")}
            onClick={() => setAction("EDIT")}
            icon={Edit3}
            label="تعديل وقفل"
          />
          {box.is_locked && (
            <Action
              onClick={() => setAction("EMERGENCY")}
              icon={AlertTriangle}
              label="فتح طارئ"
            />
          )}
          {box.scope === "FAMILY" && !owner && (
            <Action
              onClick={() => setAction("LEAVE")}
              icon={Users}
              label="مغادرة"
            />
          )}
        </div>
        {box.is_locked && (
          <p className="rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-900">
            <Lock className="me-1 inline h-4 w-4" />
            يمكنك الإضافة، لكن السحب والصرف محجوبان حتى فتح الصندوق أو موافقة
            المدير على طلب طارئ.
          </p>
        )}
        {box.scope === "FAMILY" && (
          <section>
            <h4 className="font-black">المساهمون</h4>
            <div className="mt-2 space-y-2">
              {box.contributor_summary.length ? (
                box.contributor_summary.map((row, i) => (
                  <div
                    key={`${row.user_id}${row.currency}${i}`}
                    className="flex justify-between rounded-xl border p-3 text-xs"
                  >
                    <span>{row.display_name}</span>
                    <b>{money(row.total_amount, row.currency)}</b>
                  </div>
                ))
              ) : (
                <p className="text-xs text-[var(--color-text-secondary)]">
                  لا توجد مساهمات بعد.
                </p>
              )}
            </div>
          </section>
        )}
        <section>
          <h4 className="flex items-center gap-2 font-black">
            <History className="h-4 w-4" />
            سجل الصندوق
          </h4>
          <div className="mt-2 max-h-52 space-y-2 overflow-auto">
            {box.activity?.length ? (
              box.activity.map((row) => (
                <div
                  key={row.id}
                  className="rounded-xl border border-[var(--color-border)] p-3 text-xs"
                >
                  <b>{activityLabels[row.action] || row.action}</b>
                  <span className="mt-1 block text-[var(--color-text-secondary)]">
                    {row.actor_name_snapshot} ·{" "}
                    {new Date(row.created_at).toLocaleString("ar-LB")}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-xs">لا يوجد نشاط.</p>
            )}
          </div>
        </section>
        {owner && (
          <button
            disabled={Boolean(me?.view_as?.read_only)} onClick={onDelete}
            className="w-full rounded-xl border border-red-200 py-2.5 text-sm font-bold text-red-600"
          >
            <Trash2 className="me-2 inline h-4 w-4" />
            حذف الصندوق وإعادة كل الأموال
          </button>
        )}
        {action && (
          <ActionModal
            action={action}
            box={box}
            onClose={() => setAction(null)}
            onDone={async (completed) => {
              setAction(null);
              await onRefresh(completed ? box.name : null);
            }}
          />
        )}
      </div>
    </Modal>
  );
}
function Action({ icon: Icon, label, onClick, disabled }) {
  return (
    <button
      disabled={disabled }
      onClick={onClick}
      className="min-h-20 rounded-2xl border border-[var(--color-border)] p-3 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Icon className="mx-auto mb-2 h-5 w-5 text-amber-600" />
      {label}
    </button>
  );
}

function ActionModal({ action, box, onClose, onDone }) {
  const defaultCurrency = box.type === "GOAL" ? box.target.currency : "USD",
    [form, setForm] = useState({
      amount: "",
      currency: defaultCurrency,
      category: "أخرى",
      note: "",
      reason: "",
      lock_mode: box.lock_mode,
      target_amount: box.progress?.target_amount || "",
      locked_until: box.locked_until?.slice(0, 16) || "",
    }),
    [loading, setLoading] = useState(false),
    [confirmExcess, setConfirmExcess] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let res;
      const payload = {
        ...form,
        amount:
          form.currency === "LBP"
            ? Number(form.amount || 0) * 1000
            : form.amount,
        request_id: requestId("box-action"),
      };
      if (action === "CONTRIBUTE")
        res = await userApi.post(
          `/user/boxes/${box.id}/contributions`,
          payload,
        );
      if (action === "WITHDRAW")
        res = await userApi.post(`/user/boxes/${box.id}/withdrawals`, payload);
      if (action === "SPEND")
        res = await userApi.post(`/user/boxes/${box.id}/expenses`, payload);
      if (action === "EMERGENCY")
        res = await userApi.post(`/user/boxes/${box.id}/emergency-unlock`, {
          reason: form.reason,
          request_id: requestId("unlock"),
        });
      if (action === "EDIT")
        res = await userApi.put(`/user/boxes/${box.id}`, {
          ...form,
          target_amount: box.type === "GOAL" ? form.target_amount : undefined,
          confirm_return_excess: confirmExcess,
          request_id: requestId("edit"),
        });
      if (action === "LEAVE") {
        if (
          !window.confirm(
            "مساهماتك السابقة ستبقى أموالاً مشتركة ولن تعاد تلقائياً. هل تريد المغادرة؟",
          )
        )
          return;
        res = await userApi.post(`/user/boxes/${box.id}/leave`, {
          request_id: requestId("leave"),
        });
      }
      toast.success(
        action === "EMERGENCY"
          ? "تم إرسال الطلب إلى مركز الموافقات."
          : "تمت العملية وحُفظت.",
      );
      onDone(
        res?.data?.box?.status === "COMPLETED" && box.status !== "COMPLETED",
      );
    } catch (err) {
      const d = err?.response?.data;
      if (
        d?.code === "GOAL_OVERFUND" &&
        window.confirm(
          `تحتاج فقط إلى ${money(d.remaining_amount, d.currency)}. هل تريد إضافة المبلغ المتبقي؟`,
        )
      ) {
        setForm({
          ...form,
          amount:
            d.currency === "LBP"
              ? Number(d.remaining_amount) / 1000
              : d.remaining_amount,
        });
        toast.info("تم وضع المبلغ المتبقي؛ اضغط تأكيد.");
      } else if (d?.code === "TARGET_EXCESS_CONFIRMATION") {
        setConfirmExcess(true);
        toast.warning(`${d.detail} أعد الضغط على حفظ للتأكيد.`);
      } else toast.error(d?.detail || "تعذر تنفيذ العملية.");
    } finally {
      setLoading(false);
    }
  };
  const title = {
    CONTRIBUTE: "إضافة أموال",
    WITHDRAW: "إعادة إلى البنك",
    SPEND: "صرف من الصندوق",
    EMERGENCY: "فتح طارئ",
    EDIT: "إعدادات الصندوق",
    LEAVE: "مغادرة الصندوق",
  }[action];
  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {["CONTRIBUTE", "WITHDRAW", "SPEND"].includes(action) && (
          <>
            <select
              disabled={box.type === "GOAL"}
              className="input-pro"
              value={form.currency}
              onChange={(e) =>
                setForm({ ...form, currency: e.target.value, amount: "" })
              }
            >
              <option>USD</option>
              <option>LBP</option>
            </select>
            {form.currency === "LBP" ? (
              <LbpAmountInput currency="LBP"
                required
                value={form.amount}
                onChange={(value) => setForm({ ...form, amount: value })}
              />
            ) : (
              <input
                autoFocus
                required
                type="number"
                min="0.01"
                step="0.01"
                className="input-pro"
                placeholder="المبلغ"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            )}
          </>
        )}
        {action === "SPEND" && (
          <>
            <input
              className="input-pro"
              placeholder="الفئة"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            />
            <textarea
              className="input-pro"
              placeholder="ملاحظة"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </>
        )}
        {action === "EMERGENCY" && (
          <textarea
            required
            className="input-pro min-h-28"
            placeholder="اشرح سبب الحاجة إلى فتح الصندوق"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        )}{" "}
        {action === "EDIT" && (
          <>
            <select
              className="input-pro"
              value={form.lock_mode}
              onChange={(e) => setForm({ ...form, lock_mode: e.target.value })}
            >
              <option value="UNLOCKED">غير مقفل</option>
              <option value="MANUAL">قفل يدوي</option>
              <option value="UNTIL">مقفل حتى موعد</option>
            </select>
            {form.lock_mode === "UNTIL" && (
              <input
                required
                type="datetime-local"
                className="input-pro"
                value={form.locked_until}
                onChange={(e) =>
                  setForm({ ...form, locked_until: e.target.value })
                }
              />
            )}{" "}
            {box.type === "GOAL" && (
              <input
                required
                type="number"
                step={box.target.currency === "USD" ? "0.01" : "5000"}
                className="input-pro"
                value={form.target_amount}
                onChange={(e) => {
                  setConfirmExcess(false);
                  setForm({ ...form, target_amount: e.target.value });
                }}
              />
            )}
            {confirmExcess && (
              <p className="rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-900">
                سيُعاد المبلغ الزائد إلى رصيد البنك عند الحفظ.
              </p>
            )}
          </>
        )}
        {action === "LEAVE" && (
          <p className="rounded-xl bg-amber-50 p-4 text-sm">
            لن تُعاد مساهماتك السابقة تلقائياً، وستبقى ضمن الأموال المشتركة.
          </p>
        )}
        <button disabled={loading} className="btn-navy w-full rounded-xl py-3">
          {loading ? "جارٍ التنفيذ…" : "تأكيد"}
        </button>
      </form>
    </Modal>
  );
}
function Modal({ title, onClose, children, wide = false }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        role="dialog" aria-modal="true"
        className={`max-h-[92dvh] w-full overflow-y-auto rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-2xl ${wide ? "max-w-3xl" : "max-w-lg"}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-xl font-black">{title}</h3>
          <button aria-label="إغلاق" onClick={onClose} className="h-10 w-10 rounded-xl border">
            <X className="mx-auto h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
